/**
 * src/lib/firebase.js — kết nối Firestore để đọc dữ liệu quỹ lúc chạy.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO CẦN: thêm quỹ thứ 30 mà không phải build lại và deploy lại mã nguồn.
 * Sửa trong Firebase Console là thay đổi hiện ngay với mọi người dùng.
 *
 * Dữ liệu quỹ trong src/data/funds.js vẫn giữ nguyên vai trò DỰ PHÒNG: khi
 * chưa cấu hình Firestore, khi mạng lỗi, hoặc khi collection rỗng, công cụ vẫn
 * chạy bằng dữ liệu đóng gói sẵn. Website không bao giờ hỏng vì một truy vấn.
 *
 * Dùng bản `firestore/lite` — chỉ đọc một lần, không cần theo dõi thay đổi
 * thời gian thực, và nhẹ hơn bản đầy đủ đáng kể.
 * ─────────────────────────────────────────────────────────────────────────
 */

// import.meta.env chỉ có khi chạy qua Vite; test Node import file này qua
// workspace.js → track.js nên cần giá trị dự phòng.
const ENV = import.meta.env || {};

const CAU_HINH = {
  apiKey: ENV.VITE_FIREBASE_API_KEY,
  authDomain: ENV.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: ENV.VITE_FIREBASE_PROJECT_ID,
  storageBucket: ENV.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.VITE_FIREBASE_APP_ID,
  // Chỉ dùng khi bật đo lường (xem src/lib/track.js và ANALYTICS.md).
  ...(ENV.VITE_FIREBASE_MEASUREMENT_ID
    ? { measurementId: ENV.VITE_FIREBASE_MEASUREMENT_ID }
    : {}),
};

/** Chưa khai đủ cấu hình thì bỏ qua Firestore, dùng dữ liệu đóng gói sẵn. */
export const CO_CAU_HINH = Boolean(CAU_HINH.apiKey && CAU_HINH.projectId);

let dbPromise = null;
let appPromise = null;

export function layApp() {
  if (!CO_CAU_HINH) return Promise.resolve(null);
  if (!appPromise) appPromise = import('firebase/app').then(({ initializeApp, getApps }) =>
    getApps().length ? getApps()[0] : initializeApp(CAU_HINH));
  return appPromise;
}

/**
 * Nạp Firestore theo kiểu lười: chỉ tải khi thực sự cần. Nhờ vậy 11 công cụ
 * còn lại không phải tải thư viện Firebase mà chúng không dùng tới.
 */
export function layDb() {
  if (!CO_CAU_HINH) return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = (async () => {
      const [{ getFirestore }, app] = await Promise.all([import('firebase/firestore/lite'), layApp()]);
      return getFirestore(app);
    })();
  }
  return dbPromise;
}

let storagePromise = null;
export function layStorage() {
  if (!CO_CAU_HINH) return Promise.resolve(null);
  if (!storagePromise) storagePromise = Promise.all([layApp(), import('firebase/storage')])
    .then(([app, { getStorage }]) => getStorage(app));
  return storagePromise;
}
